import axios from 'axios';
import { useUserStore } from '@/store/userStore';

const BASE_URL = import.meta.env.VITE_API_URL;

/**
 * Service for managing work order activities
 */
export const activityService = {
  /**
   * Get all activities for a work order
   * @param {string} workOrderId - Work order ID
   * @returns {Promise<{activities: Array, summary: Object}>}
   */
  async getActivities(workOrderId) {
    const userStore = useUserStore();
    const response = await axios.get(`${BASE_URL}/work-orders/${workOrderId}/activities`, {
      headers: { Authorization: `Bearer ${userStore.accessToken}` },
    });
    return response.data;
  },

  /**
   * Create a new activity for a work order
   * @param {string} workOrderId - Work order ID
   * @param {Object} activityData - Activity data
   * @returns {Promise<Object>}
   */
  async createActivity(workOrderId, activityData) {
    const userStore = useUserStore();
    const response = await axios.post(
      `${BASE_URL}/work-orders/${workOrderId}/activities`,
      activityData,
      {
        headers: { Authorization: `Bearer ${userStore.accessToken}` },
      }
    );
    return response.data;
  },

  /**
   * Add default activities to a work order
   * @param {string} workOrderId - Work order ID
   * @param {string} template - Template name (default: 'standard')
   * @returns {Promise<Array>}
   */
  async addDefaultActivities(workOrderId, template = 'standard') {
    const userStore = useUserStore();
    const response = await axios.post(
      `${BASE_URL}/work-orders/${workOrderId}/activities/defaults`,
      { template },
      {
        headers: { Authorization: `Bearer ${userStore.accessToken}` },
      }
    );
    return response.data;
  },

  /**
   * Update an activity
   * @param {string} workOrderId - Work order ID
   * @param {string} activityId - Activity ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>}
   */
  async updateActivity(workOrderId, activityId, updates) {
    const userStore = useUserStore();
    const response = await axios.patch(
      `${BASE_URL}/work-orders/${workOrderId}/activities/${activityId}`,
      updates,
      {
        headers: { Authorization: `Bearer ${userStore.accessToken}` },
      }
    );
    return response.data;
  },

  /**
   * Delete an activity
   * @param {string} workOrderId - Work order ID
   * @param {string} activityId - Activity ID
   * @returns {Promise<void>}
   */
  async deleteActivity(workOrderId, activityId) {
    const userStore = useUserStore();
    await axios.delete(`${BASE_URL}/work-orders/${workOrderId}/activities/${activityId}`, {
      headers: { Authorization: `Bearer ${userStore.accessToken}` },
    });
  },

  /**
   * Log time for an activity
   * @param {string} workOrderId - Work order ID
   * @param {string} activityId - Activity ID
   * @param {Object} logData - Time log data
   * @returns {Promise<Object>}
   */
  async logTime(workOrderId, activityId, logData) {
    const userStore = useUserStore();
    const response = await axios.post(
      `${BASE_URL}/work-orders/${workOrderId}/activities/${activityId}/log`,
      logData,
      {
        headers: { Authorization: `Bearer ${userStore.accessToken}` },
      }
    );
    return response.data;
  },

  /**
   * Reorder activities
   * @param {string} workOrderId - Work order ID
   * @param {Array<{id: string, sort_order: number}>} order - New order
   * @returns {Promise<void>}
   */
  async reorderActivities(workOrderId, order) {
    const userStore = useUserStore();
    await axios.patch(
      `${BASE_URL}/work-orders/${workOrderId}/activities/reorder`,
      { order },
      {
        headers: { Authorization: `Bearer ${userStore.accessToken}` },
      }
    );
  },
};

export default activityService;
